/**
 * WS4 session transcript data — per-tab lists of section banners and
 * speaker lines for the transcript modal.
 *
 * Ported from:
 *   - `_legacy/ws4_live_content.js` lines 1-88 — `panes.full`, `panes.act1`,
 *     `panes.act2`, `panes.act3` arrays for the WS4_TRANSCRIPT node.
 *     Vanilla embedded `<span class="trx-tag …">…</span>` HTML at the
 *     tail of each line; we strip those tags here and surface them as
 *     typed `tag` fields so the React layer can render the pill without
 *     `dangerouslySetInnerHTML`.
 *   - `_legacy/wd.html` lines 1481-1718 — rendered transcript markup
 *     with `.trx-*` and `.sp-*` classes. The `cls` field on each line
 *     (e.g. `sp-jordan`) is dropped here in favour of a speaker→class
 *     map in `TranscriptPlayback.tsx`, so the data layer stays purely
 *     semantic.
 *
 * Per-tab pane keys mirror the legacy `data-txtab` attributes: `full`,
 * `act1`, `act2`, `act3`.
 */

export type TranscriptTag = "action" | "flag" | "note" | "ai";

export type TranscriptBlock =
  | {
      kind: "line";
      id: string;
      /** mm:ss display label (kept verbatim from the vanilla source). */
      time: string;
      speaker: string;
      text: string;
      tag?: TranscriptTag;
      tagText?: string;
    }
  | {
      kind: "banner";
      id: string;
      label: string;
      time: string;
      /** Tone override for end-of-session / break banners. */
      tone?: "default" | "gold" | "green";
    };

export type TranscriptTabKey = "full" | "act1" | "act2" | "act3";

export type TranscriptTab = {
  key: TranscriptTabKey;
  label: string;
  blocks: TranscriptBlock[];
};

/* ----------------------------- Helpers --------------------------- */

function bn(
  id: string,
  label: string,
  time: string,
  tone: "default" | "gold" | "green" = "default",
): TranscriptBlock {
  return { kind: "banner", id, label, time, tone };
}

function ln(
  id: string,
  time: string,
  speaker: string,
  text: string,
  tag?: TranscriptTag,
  tagText?: string,
): TranscriptBlock {
  const block: TranscriptBlock = { kind: "line", id, time, speaker, text };
  if (tag !== undefined) {
    block.tag = tag;
  }
  if (tagText !== undefined) {
    block.tagText = tagText;
  }
  return block;
}

/* --------------------------- Full pane --------------------------- */

const fullPane: TranscriptBlock[] = [
  bn("b-full-opening", "Opening & Scene-Setting", "00:00 – 00:18"),
  ln(
    "f-0000-jordan",
    "00:00",
    "Jordan K.",
    "Good morning everyone — welcome to Workshop Four. I know some of you have been looking forward to this one, and a few of you have been quietly dreading it. Both reactions are completely valid. Today we're getting into the real meat: how do humans and AI agents actually work together. Not in theory — in your day-to-day.",
  ),
  ln(
    "f-0002-jordan",
    "00:02",
    "Jordan K.",
    'Before we dive in, quick housekeeping. XAES-AI is observing and auto-transcribing today — you\'ve all consented to that. If you say something you want redacted, just say "off record" and she\'ll pause. John C. is sitting in as our executive observer. The session runs to about 3.5 hours with a break mid-way. We have four activities and I want us to land real outputs from each one — not just post-its.',
  ),
  ln(
    "f-0004-aria",
    "00:04",
    "XAES-AI",
    "Good morning. Transcription is live. I'll flag any sections where audio confidence drops below 90%. I'll also note any decision points or action items I detect — these will be marked in the transcript as [ACTION] tags for your review.",
    "ai",
    "AI OBSERVER",
  ),
  ln(
    "f-0006-priya",
    "00:06",
    "Priya S.",
    "Jordan — quick question before we start. Are we making binding decisions today or is this still exploratory? I want to know how much weight to give our outputs.",
  ),
  ln(
    "f-0007-jordan",
    "00:07",
    "Jordan K.",
    "Great question and I should have said this upfront. Today's outputs go into a draft charter — they are directional, not binding. The Mode Assignment Matrix we build today will go to the leadership team for ratification. So take it seriously, but you won't be held to word-for-word. What we need is enough signal to make confident decisions next week.",
  ),
  ln(
    "f-0010-marcus",
    "00:10",
    "Marcus W.",
    "I'll be honest — I came in fairly sceptical. I've been in a lot of these workshops where we map things out and nothing changes. What's different this time?",
  ),
  ln(
    "f-0011-jordan",
    "00:11",
    "Jordan K.",
    "Marcus — I appreciate you saying it. The difference this time is that we already have a live deployment decision on the table. This isn't horizon planning. The agent infrastructure is being built now. If we don't define these ways of working, someone else will define them for us, and that won't serve anyone in this room.",
  ),

  bn("b-full-act1", "Activity 2.1 — Role & Responsibility Mapping", "00:18 – 01:10"),
  ln(
    "f-0018-jordan",
    "00:18",
    "Jordan K.",
    "Right. Activity 2.1. You each have a role card in front of you. In your groups, I want you to map every task in your domain onto the RACI-style grid on the whiteboard. For each task: who owns the decision, who executes, who reviews, who needs to know. The twist — you have to explicitly mark which tasks you'd be comfortable handing to an agent right now, versus tasks that need to stay human.",
  ),
  ln(
    "f-0021-sam",
    "00:21",
    "Sam R.",
    "Are we mapping current state or future state? Because our current state is basically everything is human and nothing is agent.",
  ),
  ln(
    "f-0022-jordan",
    "00:22",
    "Jordan K.",
    "Map both. Current state in blue, desired state in orange. That gap is where we focus the rest of the session. Twenty-five minutes, then we'll do a gallery walk.",
  ),
  ln(
    "f-0050-priya",
    "00:50",
    "Priya S.",
    "Okay in our group — we're seeing a pattern. Almost everything in the execution layer, we're comfortable giving to an agent. But anything that touches a client relationship or a regulatory sign-off — that stays human, full stop. Even if the agent could technically do it.",
  ),
  ln(
    "f-0052-marcus",
    "00:52",
    "Marcus W.",
    'Same in ours. But we got into a debate about monitoring. Like — if an agent is doing the execution, and a human is watching, is that human actually "reviewing" or are they just there to tick a box? Because there\'s a real risk of automation bias creeping in.',
  ),
  ln(
    "f-0053-jordan",
    "00:53",
    "Jordan K.",
    "That's a really important distinction Marcus — park that as a flag for Activity 2.3. XAES, can you note that as a discussion item?",
    "note",
    "FLAGGED",
  ),
  ln(
    "f-0054-aria",
    "00:54",
    "XAES-AI",
    'Noted. Item flagged: "Automation bias risk when human oversight is nominal rather than substantive." Tagged for Activity 2.3 scenario design.',
    "ai",
    "AI OBSERVER",
  ),
  ln(
    "f-0102-sam",
    "01:02",
    "Sam R.",
    "One thing our group flagged — there are some tasks where the agent probably performs better than the human, but the human still needs to own the outcome publicly. Compliance reporting is the obvious one. We'd let the agent do the heavy lifting but the sign-off has to be a named person.",
  ),
  ln(
    "f-0104-ben",
    "01:04",
    "John C.",
    "That's a really important distinction — I'd call it accountability ownership versus execution ownership. They don't have to be the same person — or in this case, the same entity. The agent can execute. The human accountable owner signs. But you need that to be explicit in your governance model, otherwise it'll be ambiguous the moment something goes wrong.",
    "action",
  ),
  ln(
    "f-0106-jordan",
    "01:06",
    "Jordan K.",
    "Ben — do you want us to capture that framing in the charter language?",
  ),
  ln(
    "f-0107-ben",
    "01:07",
    "John C.",
    'Yes. Please. Make it explicit. "Execution ownership" and "accountability ownership" as separate fields on every task in the matrix.',
  ),

  bn("b-full-act2", "Activity 2.2 — Operating Mode Calibration", "01:10 – 01:58"),
  ln(
    "f-0110-jordan",
    "01:10",
    "Jordan K.",
    "Activity 2.2. You've all seen the four operating modes in your pre-read: In-the-Loop, Above-the-Loop, On-the-Loop, Out-of-Loop. I want each group to take your task list from Activity 2.1 and assign a mode to each task. Be honest. Don't just default to In-the-Loop because it feels safe — genuinely interrogate whether that's the right mode or whether you're just being cautious.",
  ),
  ln(
    "f-0113-marcus",
    "01:13",
    "Marcus W.",
    "Can a task have different modes depending on context? Like — campaign content generation might be Above-the-Loop for a standard newsletter but In-the-Loop for anything going to the board.",
  ),
  ln(
    "f-0114-jordan",
    "01:14",
    "Jordan K.",
    "Yes — and that's a great nuance. If a task has context-dependent modes, mark the default mode and note the exception trigger. That becomes part of your escalation logic.",
  ),
  ln(
    "f-0140-priya",
    "01:40",
    "Priya S.",
    "We've got a split in our group on incident monitoring. Half of us think On-the-Loop is right — agent watches, alerts human when needed. The other half want In-the-Loop because if we miss something at 2am, no-one's actually watching the loop.",
  ),
  ln(
    "f-0142-aria",
    "01:42",
    "XAES-AI",
    "I can offer some context here if it's useful. In multi-agent deployment literature, 24/7 monitoring tasks are typically most effective in On-the-Loop configuration with a defined human escalation SLA — the key variable is the escalation response time commitment, not the baseline mode. The risk Priya describes — no-one watching at 2am — is a human resource coverage problem, not a mode design problem.",
    "ai",
    "AI INPUT",
  ),
  ln(
    "f-0143-marcus",
    "01:43",
    "Marcus W.",
    "That's actually a useful reframe. So the mode governs normal operations, and we separately need a coverage rota for the escalation pathway.",
  ),
  ln(
    "f-0144-priya",
    "01:44",
    "Priya S.",
    "Okay — I can accept On-the-Loop if we commit to a named escalation contact with a response SLA. That needs to go in the protocol.",
    "action",
  ),
  ln(
    "f-0150-sam",
    "01:50",
    "Sam R.",
    'We had a harder conversation about Out-of-Loop. Nobody in our group selected it for anything. Not because the tasks couldn\'t technically run autonomously — we just don\'t trust the governance model yet. There\'s no clear answer to "what happens when the agent gets it wrong" in an Out-of-Loop scenario.',
  ),
  ln(
    "f-0152-ben",
    "01:52",
    "John C.",
    "That's honest and that's the right instinct. Out-of-Loop should be earned, not assumed. I'd suggest we treat it as a maturity gate — tasks can graduate to Out-of-Loop after six months of On-the-Loop operation without a critical incident. Build that into the governance model.",
    "action",
  ),
  ln(
    "f-0154-jordan",
    "01:54",
    "Jordan K.",
    'I love that framing. "Mode graduation" — we\'re adding that to the charter language. XAES, flag that.',
  ),

  bn("b-full-break", "Break", "01:58 – 02:13", "gold"),

  bn("b-full-act3", "Activity 2.3 — Stress Test Scenarios", "02:13 – 03:08"),
  ln(
    "f-0213-jordan",
    "02:13",
    "Jordan K.",
    "Welcome back. Activity 2.3 is where we stress-test what we just built. I'm going to read out four scenarios. For each one, I want you to tell me: does your mode assignment hold under this condition? Or does it break?",
  ),
  ln(
    "f-0215-jordan",
    "02:15",
    "Jordan K.",
    "Scenario One. The agent is operating On-the-Loop on customer query triage. Volume spikes 400% due to a product recall. The agent continues to triage — but its confidence scores drop below 70% on a new category of query it hasn't seen before. No human is actively watching the dashboard. What happens?",
  ),
  ln(
    "f-0217-marcus",
    "02:17",
    "Marcus W.",
    "That's a failure mode. The agent should be configured to self-escalate when confidence drops below threshold. If it doesn't, that's a product gap, not a process gap.",
  ),
  ln(
    "f-0218-priya",
    "02:18",
    "Priya S.",
    "But who defined the threshold? Was it set during deployment? Because if it was set at 80% confidence and someone lowered it to 60% to reduce alert noise, that's a governance failure right there.",
  ),
  ln(
    "f-0219-aria",
    "02:19",
    "XAES-AI",
    "Priya raises a documented risk pattern. Threshold drift — the progressive lowering of escalation sensitivity to reduce noise — is one of the top three root causes of agentic incidents in deployment post-mortems. I'd recommend that threshold changes require a two-person sign-off and are logged in the audit trail.",
    "flag",
    "RISK FLAG",
  ),
  ln(
    "f-0221-sam",
    "02:21",
    "Sam R.",
    'Can we make that a design principle? "Threshold changes require dual sign-off and are permanently logged." That should be in the charter.',
  ),
  ln(
    "f-0222-jordan",
    "02:22",
    "Jordan K.",
    "Agreed. XAES — action for Ops Lead.",
    "action",
  ),
  ln(
    "f-0225-jordan",
    "02:25",
    "Jordan K.",
    "Scenario Two. The agent is Above-the-Loop on campaign content. It generates a social post that is technically on-brief, grammatically correct — but culturally tone-deaf given a news event that broke an hour ago. The post goes live before the human reviewer sees it.",
  ),
  ln(
    "f-0227-marcus",
    "02:27",
    "Marcus W.",
    "That's terrifying. And realistic. Our current process has a 4-hour review window. An agent could publish inside that window on a time-sensitive brief.",
  ),
  ln(
    "f-0228-priya",
    "02:28",
    "Priya S.",
    "This is exactly why I argued for In-the-Loop on anything external-facing. The reputational risk is too high. You can't retrieve a tweet.",
  ),
  ln(
    "f-0230-sam",
    "02:30",
    "Sam R.",
    "I think the answer is a pre-publish hold window — nothing external goes live without a minimum 30-minute human review period, regardless of mode. The agent drafts, but the publish action has a mandatory delay.",
  ),
  ln(
    "f-0231-ben",
    "02:31",
    "John C.",
    'That\'s a sensible guardrail. Let\'s build "mandatory publish delay" into the external communications protocol. And I\'d add a news-monitoring integration — if the agent detects a breaking news signal that\'s thematically related to queued content, it should automatically flag for human review.',
    "action",
  ),
  ln(
    "f-0245-jordan",
    "02:45",
    "Jordan K.",
    "Scenario Three — and this is the one Marcus flagged earlier. The agent is In-the-Loop on compliance reporting. A human reviews and approves every output. Over six months, the human reviewer starts rubber-stamping because they've never seen an error. Then the agent produces a report with a material error — and the human approves it without reading it.",
  ),
  ln(
    "f-0247-marcus",
    "02:47",
    "Marcus W.",
    "That's the automation bias scenario. And the uncomfortable truth is that In-the-Loop doesn't protect you from it. The mode says a human reviews — it doesn't say the human reviews meaningfully.",
  ),
  ln(
    "f-0249-aria",
    "02:49",
    "XAES-AI",
    'I can contribute a potential mitigation. Randomised "red team" injections — where I deliberately introduce a minor flagged error into approximately 2% of outputs — would maintain reviewer alertness over time. The reviewer doesn\'t know which outputs are tested. This is a pattern used in high-stakes AI review workflows in healthcare and financial services.',
    "ai",
    "AI INPUT",
  ),
  ln(
    "f-0251-sam",
    "02:51",
    "Sam R.",
    "That's a fascinating idea. But it needs to be fully disclosed to reviewers upfront — they need to know they're being tested, just not when.",
  ),
  ln(
    "f-0252-marcus",
    "02:52",
    "Marcus W.",
    "I'd support piloting that. But it needs governance sign-off — you can't run that unilaterally.",
    "action",
  ),
  ln(
    "f-0304-jordan",
    "03:04",
    "Jordan K.",
    "We're running a little over on this activity — intentionally, because these conversations are important. Let me flag that we're now 10 minutes behind and we need to move to Activity 2.4. We'll carry the unresolved threads — Scenario Four and the edge cases on Scenario Three — into the action log. XAES, please note the open items.",
    "note",
  ),

  bn("b-full-end", "Session Closed", "03:43 — Auto-transcript compiled by XAES-AI", "green"),
];

/* --------------------------- Activity 1 -------------------------- */

const act1Pane: TranscriptBlock[] = [
  bn("b-act1", "Activity 2.1 — Role & Responsibility Mapping", "00:18 – 01:10"),
  ln(
    "a1-0018-jordan",
    "00:18",
    "Jordan K.",
    "Right. Activity 2.1. You each have a role card in front of you. In your groups, I want you to map every task in your domain onto the RACI-style grid on the whiteboard. For each task: who owns the decision, who executes, who reviews, who needs to know. The twist — you have to explicitly mark which tasks you'd be comfortable handing to an agent right now, versus tasks that need to stay human.",
  ),
  ln(
    "a1-0021-sam",
    "00:21",
    "Sam R.",
    "Are we mapping current state or future state? Because our current state is basically everything is human and nothing is agent.",
  ),
  ln(
    "a1-0022-jordan",
    "00:22",
    "Jordan K.",
    "Map both. Current state in blue, desired state in orange. That gap is where we focus the rest of the session. Twenty-five minutes, then we'll do a gallery walk.",
  ),
  ln(
    "a1-0050-priya",
    "00:50",
    "Priya S.",
    "Almost everything in the execution layer, we're comfortable giving to an agent. But anything that touches a client relationship or a regulatory sign-off — that stays human, full stop.",
  ),
  ln(
    "a1-0052-marcus",
    "00:52",
    "Marcus W.",
    "We got into a debate about monitoring. If an agent is doing the execution and a human is watching, is that human actually reviewing or are they just there to tick a box? Automation bias risk is real.",
    "flag",
    "FLAGGED FOR ACT.3",
  ),
  ln(
    "a1-0104-ben",
    "01:04",
    "John C.",
    "Accountability ownership versus execution ownership — they don't have to be the same entity. The agent can execute. The human accountable owner signs. But you need that to be explicit in your governance model.",
    "action",
    "ACTION: ADD TO CHARTER",
  ),
  ln(
    "a1-0107-ben",
    "01:07",
    "John C.",
    'Please make it explicit. "Execution ownership" and "accountability ownership" as separate fields on every task in the matrix.',
  ),
];

/* --------------------------- Activity 2 -------------------------- */

const act2Pane: TranscriptBlock[] = [
  bn("b-act2", "Activity 2.2 — Operating Mode Calibration", "01:10 – 01:58"),
  ln(
    "a2-0110-jordan",
    "01:10",
    "Jordan K.",
    "Take your task list from Activity 2.1 and assign a mode to each task — In-the-Loop, Above-the-Loop, On-the-Loop, or Out-of-Loop. Genuinely interrogate whether that's the right mode or whether you're just being cautious.",
  ),
  ln(
    "a2-0113-marcus",
    "01:13",
    "Marcus W.",
    "Can a task have different modes depending on context? Campaign content might be Above-the-Loop for a standard newsletter but In-the-Loop for anything going to the board.",
  ),
  ln(
    "a2-0114-jordan",
    "01:14",
    "Jordan K.",
    "Yes — mark the default mode and note the exception trigger. That becomes part of your escalation logic.",
  ),
  ln(
    "a2-0140-priya",
    "01:40",
    "Priya S.",
    "We've got a split on incident monitoring. Half want On-the-Loop, half want In-the-Loop because nobody's watching the dashboard at 2am.",
  ),
  ln(
    "a2-0142-aria",
    "01:42",
    "XAES-AI",
    "The 2am risk is a human coverage problem, not a mode design problem. On-the-Loop with a defined escalation SLA and a named coverage rota is the right pattern here.",
    "ai",
    "AI INPUT",
  ),
  ln(
    "a2-0144-priya",
    "01:44",
    "Priya S.",
    "I can accept On-the-Loop if we commit to a named escalation contact with a response SLA in the protocol.",
    "action",
  ),
  ln(
    "a2-0152-ben",
    "01:52",
    "John C.",
    "Out-of-Loop should be earned, not assumed. Treat it as a maturity gate — tasks can graduate to Out-of-Loop after six months of On-the-Loop operation without a critical incident.",
    "action",
    "ACTION: MODE GRADUATION POLICY",
  ),
];

/* --------------------------- Activity 3 -------------------------- */

const act3Pane: TranscriptBlock[] = [
  bn("b-act3", "Activity 2.3 — Stress Test Scenarios", "02:13 – 03:08"),
  ln(
    "a3-0215-jordan",
    "02:15",
    "Jordan K.",
    "Scenario 1: Agent On-the-Loop on customer query triage. Volume spikes 400%. Agent confidence drops below 70% on an unseen query category. No human is actively watching. What happens?",
  ),
  ln(
    "a3-0218-priya",
    "02:18",
    "Priya S.",
    "If someone lowered the confidence threshold to reduce alert noise, that's a governance failure. Threshold changes must require dual sign-off and be logged permanently.",
    "action",
    "ACTION: THRESHOLD GOVERNANCE RULE",
  ),
  ln(
    "a3-0219-aria",
    "02:19",
    "XAES-AI",
    "Threshold drift is one of the top three root causes of agentic incidents in post-mortems. Recommend two-person sign-off and permanent audit logging for all threshold changes.",
    "flag",
    "RISK FLAG",
  ),
  ln(
    "a3-0225-jordan",
    "02:25",
    "Jordan K.",
    "Scenario 2: Agent Above-the-Loop on campaign content generates a tone-deaf social post given a breaking news event. Post goes live before the human reviewer sees it.",
  ),
  ln(
    "a3-0230-sam",
    "02:30",
    "Sam R.",
    "Mandatory 30-minute human review window before any external publish, regardless of mode. Agent drafts, but the publish action has a mandatory delay.",
    "action",
    "ACTION: PUBLISH HOLD WINDOW",
  ),
  ln(
    "a3-0231-ben",
    "02:31",
    "John C.",
    "Add a news-monitoring integration — if the agent detects a breaking news signal thematically related to queued content, it should automatically flag for human review.",
    "action",
    "ACTION: NEWS SIGNAL INTEGRATION",
  ),
  ln(
    "a3-0245-jordan",
    "02:45",
    "Jordan K.",
    "Scenario 3: Human reviewer rubber-stamps In-the-Loop compliance reports over six months. Agent produces a material error. Human approves without reading it.",
  ),
  ln(
    "a3-0247-marcus",
    "02:47",
    "Marcus W.",
    "In-the-Loop doesn't protect you from automation bias. The mode says a human reviews — it doesn't say the human reviews meaningfully.",
  ),
  ln(
    "a3-0249-aria",
    "02:49",
    "XAES-AI",
    "Proposed mitigation: randomised red-team injections — I deliberately introduce a minor flagged error into ~2% of outputs to maintain reviewer alertness. Reviewer is aware they'll be tested, but not when. Used in healthcare and financial services.",
    "ai",
    "AI INPUT",
  ),
  ln(
    "a3-0252-marcus",
    "02:52",
    "Marcus W.",
    "Support piloting that — but needs governance sign-off. Can't run it unilaterally.",
    "action",
    "ACTION: RED TEAM PILOT PROPOSAL",
  ),
  ln(
    "a3-0304-jordan",
    "03:04",
    "Jordan K.",
    "We're 10 minutes over. Scenario 4 and remaining edge cases carried into action log.",
    "note",
    "NOTE: OPEN ITEMS LOGGED",
  ),
];

/* ----------------------------- Tabs ------------------------------ */

export const WS4_TRANSCRIPT_TABS: TranscriptTab[] = [
  { key: "full", label: "Full Transcript", blocks: fullPane },
  { key: "act1", label: "Activity 2.1 — Role Mapping", blocks: act1Pane },
  { key: "act2", label: "Activity 2.2 — Mode Calibration", blocks: act2Pane },
  { key: "act3", label: "Activity 2.3 — Stress Test", blocks: act3Pane },
];

/** Per-workshop transcript tabs. Only WS4 is authored; others have no
 *  session transcript yet. */
const TRANSCRIPT_BY_WORKSHOP: Partial<Record<string, TranscriptTab[]>> = {
  WS4: WS4_TRANSCRIPT_TABS,
};

/**
 * P0.4 — public transcript getter (Phase-0 content-getter contract).
 * Returns the workshop's transcript tabs, or `null` when none is
 * authored (the caller renders a per-workshop empty state). This is the
 * seam that breaks the S3↔B2 circular dependency: consumers import the
 * signature without depending on the WS4 data shape directly.
 * `TranscriptPlayback` reads through this (RT-GALLERY) instead of the
 * direct `WS4_TRANSCRIPT_TABS` import.
 */
export function getTranscript(workshopId: string): TranscriptTab[] | null {
  return TRANSCRIPT_BY_WORKSHOP[workshopId] ?? null;
}
